import { BaseSlider } from '@theme/component-slider';

/**
 * @class ProductInfo
 * @extends HTMLElement
 */

if (!customElements.get("od-product-info")) {
  class ProductInfo extends HTMLElement {
    constructor() {
      super();

      this._onVariantChanged = this._onVariantChange.bind(this);
    }

    get enableHistoryState() {
      return this.getAttribute("enable-history-state") === "true";
    }

    connectedCallback() {
      this.productForm = document.forms[this.getAttribute("form")];
      this.productForm?.addEventListener("variant:change", this._onVariantChanged);
    }

    disconnectedCallback() {
      this.productForm?.removeEventListener("variant:change", this._onVariantChanged);
    }

    _onVariantChange(event) {
      const variant = event.detail.variant;

      if (!variant) {
        return;
      }

      this._updateBrowserHistoryState(variant);
    }

    _updateBrowserHistoryState(variant) {
      if (!this.enableHistoryState) {
        return;
      }

      const url = theme.getProductUrlWithVariant(window.location.href, variant.id);
      window.history.replaceState({ path: url }, "", url);
    }
  }

  customElements.define("od-product-info", ProductInfo);
}

/**
 * @class ProductSlider
 * @extends BaseSlider
 */

if (!customElements.get("od-product-slider")) {
  class ProductSlider extends BaseSlider {
    constructor() {
      super();

      this._onSlideChanged = this.onSlideChange.bind(this);
      this._onThumbnailKeydown = this.onThumbnailKeydown.bind(this);
    }

    connectedCallback() {
      super.connectedCallback();
      this.enhanceThumbnails();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      clearTimeout(this.thumbnailsRevealTimer);
      this.thumbnailsElement?.removeEventListener("keydown", this._onThumbnailKeydown);
    }

    get thumbnailsElement() {
      const selector = this.getAttribute("thumbnails");
      return selector ? document.querySelector(selector) : null;
    }

    // Reveal the first row of thumbnails together once they have all loaded
    // (or after a short timeout), instead of each popping in on its own.
    // Later, off-screen thumbnails fade in individually as they load.
    enhanceThumbnails() {
      const thumbnails = this.thumbnailsElement;
      if (!thumbnails) return;

      const images = [...thumbnails.querySelectorAll(".product-media-item__image--thumbnail")];
      const firstRow = images.filter((image) => image.getAttribute("loading") === "eager");
      let pending = firstRow.length;

      const reveal = () => {
        clearTimeout(this.thumbnailsRevealTimer);
        thumbnails.setAttribute("data-ready", "");
      };

      images.forEach((image) => {
        const markLoaded = () => {
          image.classList.add("is-loaded");
          if (firstRow.includes(image) && --pending === 0) reveal();
        };

        if (image.complete) {
          markLoaded();
        } else {
          image.addEventListener("load", markLoaded, { once: true });
          image.addEventListener("error", markLoaded, { once: true });
        }
      });

      if (pending <= 0) reveal();
      else this.thumbnailsRevealTimer = setTimeout(reveal, 1500);

      thumbnails.setAttribute("data-fade-in", "");
      thumbnails.addEventListener("keydown", this._onThumbnailKeydown);
    }

    onThumbnailKeydown(event) {
      if (event.key !== "Enter" && event.key !== " ") return;

      const button = event.target.closest("[data-thumbnail-index]");
      if (!button) return;

      event.preventDefault();
      this.swiperInstance?.slideTo(Number(button.dataset.thumbnailIndex));
    }

    syncCurrentThumbnail() {
      const activeIndex = this.swiperInstance?.activeIndex;

      this.thumbnailsElement?.querySelectorAll("[data-thumbnail-index]").forEach((button) => {
        if (Number(button.dataset.thumbnailIndex) === activeIndex) {
          button.setAttribute("aria-current", "true");
        } else {
          button.removeAttribute("aria-current");
        }
      });
    }

    get options() {
      const thumbnailsSelector = this.getAttribute("thumbnails");
      const thumbnailsElement = thumbnailsSelector ? document.querySelector(thumbnailsSelector) : null;
      const thumbnailsSwiperInstance = thumbnailsElement?.swiperInstance;

      return {
        ...super.options,
        on: {
          slideChange: this._onSlideChanged
        },
        ...(thumbnailsSwiperInstance && {
          thumbs: {
            swiper: thumbnailsSwiperInstance
          }
        })
      }
    }

    onSlideChange() {
      this.syncCurrentThumbnail();

      const previousSlide = this.slides.find((slide) => {
        return parseInt(slide.getAttribute("data-index")) === parseInt(this.swiperInstance.previousIndex)
      });

      const videoPlayerElement = previousSlide.querySelector("video-player");

      if (videoPlayerElement) {
        videoPlayerElement.pause();
      }
    }
  }

  customElements.define("od-product-slider", ProductSlider);
}
